import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraVerificationInput } from "../../../components/AtiraVerificationInput";
import { Button } from "../../../components/Button";
import { CopyButton } from "../../../components/CopyButton";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { TotpSetupDto } from "../../../model/user/dto/TotpSetupDto";
import { TwoFactorMethod } from "../../../model/user/types/TwoFactorMethod";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { TestIDs } from "../../../tests/types/testIds";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Switch } from "../../../utils/local-antd";
import { SettingsWrapper } from "../components/SettingsWrapper";

const Row = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing.m};
`;

const ModalButton = styled(Button)`
  width: 100%;
  margin: ${Spacing.m} 0 0 0;
`;

const QrImage = styled.img`
  align-self: center;
  width: 12rem;
  height: 12rem;
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.white};
`;

const SecretRow = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};

  input {
    font-family: monospace;
    direction: ltr;
  }
`;

type Flow =
  | null
  | "email-password"
  | "email-code"
  | "email-disable"
  | "totp-password"
  | "totp-setup"
  | "totp-disable";

const PASSWORD_FLOWS: Flow[] = [
  "email-password",
  "email-disable",
  "totp-password",
  "totp-disable",
];

export const SettingsTwoFactor: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const config = useAppSelector(userSliceSelectors.selectTwoFactorConfig);
  const emailOn = Boolean(config?.methods?.includes(TwoFactorMethod.EMAIL));
  const totpOn = Boolean(config?.methods?.includes(TwoFactorMethod.TOTP));

  const [flow, setFlow] = useState<Flow>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpSetup, setTotpSetup] = useState<TotpSetupDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const isCodeStep = flow === "email-code" || flow === "totp-setup";
  const isDisable = flow === "email-disable" || flow === "totp-disable";

  const closeFlow = () => {
    setFlow(null);
    setPassword("");
    setCode("");
    setTotpSetup(null);
    setLoading(false);
  };

  const run = async (action: () => Promise<void>) => {
    try {
      setLoading(true);
      await action();
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = () =>
    run(async () => {
      switch (flow) {
        case "email-password":
          await dispatch(userActions.enableEmail2fa({ password })).unwrap();
          setFlow("email-code");
          return;
        case "totp-password":
          setTotpSetup(
            await dispatch(userActions.initTotp2fa({ password })).unwrap(),
          );
          setFlow("totp-setup");
          return;
        case "email-disable":
          await dispatch(userActions.disableEmail2fa({ password })).unwrap();
          AtiraToast.success(
            t("settings.securityPassword.twoFactor.email.disabled.toast"),
          );
          closeFlow();
          return;
        case "totp-disable":
          await dispatch(userActions.disableTotp2fa({ password })).unwrap();
          AtiraToast.success(
            t("settings.securityPassword.twoFactor.totp.disabled.toast"),
          );
          closeFlow();
          return;
      }
    });

  const submitCode = () =>
    run(async () => {
      if (flow === "totp-setup") {
        await dispatch(userActions.confirmTotp2fa({ code })).unwrap();
        AtiraToast.success(
          t("settings.securityPassword.twoFactor.totp.enabled.toast"),
        );
      } else {
        await dispatch(userActions.confirmEmail2fa({ code })).unwrap();
        AtiraToast.success(
          t("settings.securityPassword.twoFactor.enabled.toast"),
        );
      }
      closeFlow();
    });

  const resendEmailCode = async () => {
    await dispatch(userActions.enableEmail2fa({ password })).unwrap();
  };

  const modalTitle = useMemo(() => {
    switch (flow) {
      case "email-disable":
        return t("settings.securityPassword.twoFactor.email.disable.title");
      case "totp-disable":
        return t("settings.securityPassword.twoFactor.totp.disable.title");
      case "totp-password":
      case "totp-setup":
        return t("settings.securityPassword.twoFactor.totp.enable.title");
      default:
        return t("settings.securityPassword.twoFactor.enable.title");
    }
  }, [flow, t]);

  const passwordPrompt = useMemo(() => {
    const lastMethod = [emailOn, totpOn].filter(Boolean).length === 1;

    if (isDisable) {
      return lastMethod
        ? t("settings.securityPassword.twoFactor.disable.passwordPrompt")
        : t("settings.securityPassword.twoFactor.disableMethod.passwordPrompt");
    }

    return t("settings.securityPassword.twoFactor.enable.passwordPrompt");
  }, [emailOn, totpOn, isDisable, t]);

  useEffect(() => {
    dispatch(userActions.getTwoFactorConfig())
      .unwrap()
      .catch(console.log)
      .finally(() => setConfigLoaded(true));
  }, [dispatch]);

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>
        {t("settings.securityPassword.twoFactor.title")}
      </Text>

      <Info info={t("settings.securityPassword.twoFactor.description")} />

      <Row>
        <Flex flexDirection="column" gap="xs">
          <Text fontWeight="bold" fontSize="s">
            {t("settings.securityPassword.twoFactor.totp.label")}
          </Text>
          <Text color="gray" fontSize="xs">
            {t("settings.securityPassword.twoFactor.totp.description")}
          </Text>
        </Flex>

        <Switch
          checked={totpOn}
          onChange={(checked) =>
            setFlow(checked ? "totp-password" : "totp-disable")
          }
          // Until the config loads every toggle reads "off"; block clicks so a
          // quick one can't fire the wrong (enable vs disable) flow.
          loading={!configLoaded}
          data-testid={TestIDs.general.settings.twoFactor.totpToggle}
        />
      </Row>

      <Row>
        <Flex flexDirection="column" gap="xs">
          <Text fontWeight="bold" fontSize="s">
            {t("settings.securityPassword.twoFactor.email.label")}
          </Text>
          <Text color="gray" fontSize="xs">
            {t("settings.securityPassword.twoFactor.email.description")}
          </Text>
        </Flex>

        <Switch
          checked={emailOn}
          onChange={(checked) =>
            setFlow(checked ? "email-password" : "email-disable")
          }
          loading={!configLoaded}
          data-testid={TestIDs.general.settings.twoFactor.toggle}
        />
      </Row>

      {totpOn && !emailOn ? (
        <Info
          color="warning"
          info={t("settings.securityPassword.twoFactor.lockoutWarning")}
        />
      ) : null}

      <Modal
        open={flow !== null}
        onClose={closeFlow}
        title={modalTitle}
        // A code was already emailed / a QR already issued - don't let a stray
        // click-outside drop it. Closing is X-only, and the X confirms first.
        maskClosable={isCodeStep ? false : undefined}
        keyboard={!isCodeStep}
        forceClosable
        showCloseWarning={isCodeStep}
        closeWarningDescription={
          flow === "totp-setup"
            ? t("settings.securityPassword.twoFactor.totp.cancel.warning")
            : t("settings.securityPassword.twoFactor.cancel.warning")
        }
        destroyOnHidden
      >
        <Flex flexDirection="column" width={"100%"} gap="s">
          {PASSWORD_FLOWS.includes(flow) ? (
            <>
              <Text fontSize="s" color="gray">
                {passwordPrompt}
              </Text>

              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                title={t("common.password")}
                required
                autoComplete="current-password"
                dataTestId={TestIDs.general.settings.twoFactor.password}
              />

              <ModalButton
                loading={loading}
                disabled={!password}
                onClick={submitPassword}
                dataTestId={
                  isDisable
                    ? TestIDs.general.settings.twoFactor.disableConfirm
                    : TestIDs.general.settings.twoFactor.passwordSubmit
                }
                title={isDisable ? t("common.confirm") : t("common.next")}
              />
            </>
          ) : null}

          {flow === "totp-setup" && totpSetup ? (
            <>
              <Text fontSize="s" color="gray">
                {t(
                  "settings.securityPassword.twoFactor.totp.enable.scanPrompt",
                )}
              </Text>

              <QrImage
                src={totpSetup.qrDataUri}
                alt={t("settings.securityPassword.twoFactor.totp.enable.qrAlt")}
                data-testid={TestIDs.general.settings.twoFactor.totpQr}
              />

              <Text fontSize="xs" color="gray">
                {t(
                  "settings.securityPassword.twoFactor.totp.enable.manualKeyLabel",
                )}
              </Text>

              <SecretRow>
                <Input
                  readOnly
                  value={totpSetup.secret}
                  dataTestId={TestIDs.general.settings.twoFactor.totpSecret}
                />
                <CopyButton copy={totpSetup.secret} iconSize="lg" />
              </SecretRow>

              <Text fontSize="s" color="gray">
                {t(
                  "settings.securityPassword.twoFactor.totp.enable.codePrompt",
                )}
              </Text>
            </>
          ) : null}

          {flow === "email-code" ? (
            <Text fontSize="s" color="gray">
              {t("settings.securityPassword.twoFactor.enable.codePrompt")}
            </Text>
          ) : null}

          {isCodeStep ? (
            <>
              <div data-testid={TestIDs.general.settings.twoFactor.code}>
                <AtiraVerificationInput
                  value={code}
                  onChange={setCode}
                  onResendToken={
                    flow === "email-code" ? resendEmailCode : undefined
                  }
                />
              </div>

              <ModalButton
                loading={loading}
                disabled={code.length < 6}
                onClick={submitCode}
                dataTestId={TestIDs.general.settings.twoFactor.codeSubmit}
                title={t("common.confirm")}
              />
            </>
          ) : null}
        </Flex>
      </Modal>
    </SettingsWrapper>
  );
};
